/**
 * Thai translations for RFM Analysis feature
 *
 * Centralized translation constants to ensure consistency across all RFM components
 */

/**
 * Page-level translations
 */
export const PAGE_TRANSLATIONS = {
  title: "การแบ่งกลุ่มลูกค้า RFM",
  subtitle: "วิเคราะห์คุณค้าลูกค้าตามพฤติกรรม Recency (ความล่าสุด), Frequency (ความถี่), และ Monetary (มูลค่า)",

  // Period selection
  analysisPeriod: "ช่วงเวลาวิเคราะห์:",
  rollingWindow: "(ข้อมูล 18 เดือนย้อนหลัง)",

  // Loading and error states
  loadingAnalysis: "กำลังโหลดข้อมูลการวิเคราะห์ RFM...",
  loadingFailed: "โหลดข้อมูลการวิเคราะห์ RFM ไม่สำเร็จ",

  // Section headers
  customerSegments: "กลุ่มลูกค้า",
  segmentDetails: "รายละเอียดกลุ่มลูกค้า",

  // Actions
  exportAnalysis: "ส่งออกข้อมูลการวิเคราะห์",
  close: "ปิด",
  viewCustomers: "ดูรายชื่อลูกค้า"
} as const;

/**
 * Overview stats card translations
 */
export const STATS_TRANSLATIONS = {
  totalCustomers: "ลูกค้าทั้งหมด",
  acrossSegments: "กลุ่ม",
  totalRevenue: "ยอดขายรวม",
  analysisPeriod: "ช่วง 18 เดือน",
  topSegment: "กลุ่มลูกค้าอันดับ 1",
  customers: "ลูกค้า",
  distribution: "การกระจาย"
} as const;

/**
 * Segment card translations
 */
export const SEGMENT_CARD_TRANSLATIONS = {
  customers: "ลูกค้า",
  totalRevenue: "ยอดขายรวม",
  avgRevenue: "ยอดขายเฉลี่ย",
  keyAction: "แนวทางแนะนำ",
  clickToView: "คลิกเพื่อดูรายชื่อลูกค้าทั้งหมด →"
} as const;

/**
 * RFM Matrix translations
 */
export const MATRIX_TRANSLATIONS = {
  title: "ตารางกระจายข้อมูล RFM",
  recency: "Recency (ความล่าสุด)",
  frequency: "Frequency (ความถี่)",
  monetary: "Monetary (มูลค่า)",
  recencyScore: "คะแนน Recency",
  frequencyScore: "คะแนน Frequency",
  monetaryScore: "คะแนน Monetary",
  customerDensity: "ความหนาแน่นของลูกค้า",
  zero: "ไม่มี",
  low: "ต่ำ",
  medium: "ปานกลาง",
  high: "สูง",
  veryHigh: "สูงมาก",
  tip: "คำแนะนำ:",
  tipDescription: "คลิกที่ช่องใดๆ เพื่อกรองลูกค้าที่มีคะแนนชุดนั้น คะแนนสูง (มุมขวาบน) หมายถึงลูกค้าที่มีคุณค่ามากกว่า"
} as const;

/**
 * Segment map translations
 */
export const SEGMENT_MAP_TRANSLATIONS = {
  title: "แผนที่กลุ่มลูกค้า RFM",
  subtitle: "กลุ่มลูกค้าจากคะแนน Frequency (↑) และ Recency (→)",
  frequencyAndMonetary: "คะแนน Frequency และ Monetary",
  segmentTiers: "ระดับกลุ่มลูกค้า",
  highValue: "มูลค่าสูง",
  growing: "กำลังเติบโต",
  atRisk: "เสี่ยง",
  lostInactive: "สูญเสีย/ไม่ใช้งาน",
  howToRead: "วิธีอ่าน:",
  howToReadDescription: "แต่ละกล่องแสดงถึงกลุ่มลูกค้า ตำแหน่งแสดง Frequency (แนวตั้ง) เทียบกับ Recency (แนวนอน) มุมบนขวา = มีคุณค่ามากที่สุด คลิกกลุ่มใดก็ได้เพื่อดูรายชื่อลูกค้า"
} as const;

/**
 * Export button translations
 */
export const EXPORT_TRANSLATIONS = {
  exportSegment: "ส่งออกกลุ่ม",
  exportAnalysis: "ส่งออกการวิเคราะห์",
  exportAll: "ส่งออกทั้งหมด",
  exporting: "กำลังส่งออก..."
} as const;

/**
 * Customer list translations
 */
export const CUSTOMER_LIST_TRANSLATIONS = {
  title: "รายชื่อลูกค้า",
  customers: "ลูกค้า",
  customer: "ลูกค้า",
  score: "คะแนน",
  revenue: "ยอดขาย",
  purchases: "จำนวนซื้อ",
  lastPurchase: "ซื้อล่าสุด",
  daysAgo: "วันที่แล้ว",
  noCustomers: "ไม่พบลูกค้าในกลุ่มนี้",

  // Pagination
  showing: "แสดง",
  to: "ถึง",
  of: "จาก",
  previous: "ก่อนหน้า",
  next: "ถัดไป",
  page: "หน้า"
} as const;

/**
 * RFM component labels (short forms used in tables and scores)
 */
export const RFM_LABELS = {
  r: "R",  // Keep English for brevity in tables
  f: "F",
  m: "M",
  recency: "ความล่าสุด",
  frequency: "ความถี่",
  monetary: "มูลค่า"
} as const;

/**
 * Priority level translations
 */
export const PRIORITY_TRANSLATIONS = {
  Critical: "สำคัญสูงสุด",
  High: "สำคัญมาก",
  Medium: "ปานกลาง",
  Low: "ต่ำ"
} as const;

/**
 * Segment descriptions (Thai)
 */
export const SEGMENT_DESCRIPTIONS = {
  Champions: "ซื้อเยอะ/มูลค่าสูง ซื้อล่าสุดไม่นานมานี้",
  "Loyal Customers": "ซื้อเยอะ/มูลค่าสูง มีการซื้อซ้ำต่อเนื่อง",
  "Potential Loyalists": "ปริมาณ/มูลค่าการซื้อปานกลาง เพิ่งมีการซื้อล่าสุดเร็ว ๆ นี้",
  "New Customers": "เพิ่งซื้อออเดอร์แรกไปไม่นาน ปริมาณ/มูลค่าการซื้อยังต่ำอยู่",
  Promising: "ซื้อน้อย/มูลค่าต่ำ แต่ให้ความสนใจสินค้าของแบรนด์",
  "Need Attention": "ยอดการซื้อปานกลาง ไม่ซื้อมาสักพักแล้ว",
  "About to Sleep": "ซื้อน้อย/มูลค่าต่ำ ไม่ซื้อมาสักพักแล้ว",
  "Can't Lose Them": "ซื้อเยอะ/มูลค่าสูง ซื้อล่าสุดนานแล้ว",
  "At Risk": "ยอดการซื้อปานกลาง ไม่กลับมาซื้อนานแล้ว",
  Hibernating: "ซื้อน้อย/มูลค่าต่ำ ไม่กลับมาซื้อนานแล้ว",
  Lost: "ซื้อแล้วไม่กลับมาซื้อซ้ำ อาจเลิกใช้แบรนด์เราไปแล้ว"
} as const;

/**
 * Helper function to format bilingual text
 * @param thai Thai text
 * @param english English text
 * @returns Formatted bilingual string
 */
export function formatBilingual(thai: string, english: string): string {
  return `${thai} (${english})`;
}

/**
 * Helper function to format currency in Thai format
 */
export function formatThaiCurrency(amount: number): string {
  return new Intl.NumberFormat('th-TH', {
    style: 'currency',
    currency: 'THB',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}

/**
 * Helper function to format date in YYYY-MM-DD format
 */
export function formatThaiDate(dateString: string | number | Date | null): string {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
